/* eslint-disable css-modules/no-unused-class, no-nested-ternary  */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LeftSideExpandedPanel, MenuItems, RightSidePanel } from 'learning-panel-components';
import { coreService } from 'network-utils';
import {
    selectToken,
    selectLocale,
    selectIsLeanUser,
    selectCustomerDisplayName,
    selectUserNickname,
    selectUserFullName,
    selectIsPLG,
    selectIsCxUniversity
} from '@redux/slices/userSlice';
import { translate } from 'translations';
import Course from './course/courseContainer';
import Play from './play/playContainer';
import TopBannerContainer from './layout/topBar/topBarContainer';
import AccountSettingsContainer from './accountSettings/accountSettingsContainer';
import LearnPageContainer from './learn/learnPageContainer';
import PrimaryCourseContainer from './primaryCourse/primaryCourseContainer';
import TournamentsPage from './tournament/tournamentResultPage/tournamentsPage';
import PrimaryCourseCloseContainer from './primaryCourse/primaryCourseCloseMode/primaryCourseContainer';
import AssessmentPageContainer from './assessments/assessmentPageContainer';
import ChallengeLeaderboard from './play/leaderboard/challengeLeaderboard';
import Leaderboard from './play/leaderboard/userLeaderboard/leaderboard';
import PrimaryCourseDialog from './home/primaryCourseDialog/primaryCourseDialog';
import { useNotificationsWebsocket } from '../queries/notifications/notifications';
import LearningPathContainer from './learningPath/learningPathContainer';
import LeanBanner from './layout/leanBanner/leanBanner';
import VideoTutorialPopup from './learn/videoTutorials/videoTutorialContainer';
import styles from './learningPanel.module.scss';
import SkillTreeNavigationContainer from './skillTree/skillTreeNavigation/skillTreeNavigationContainer';
import ContactSupportContainer from './contactSupport/contactSupportContainer';
import HomeContainer from './home/homeContainer';
import QuizContainer from './quizPlayer/quizContainer';
import CertificatePopupContainer from './home/certificates/certificatePopupContainer';
import DiscoverContainer from './discover/discoverContainer';

const AppContainer = () => {
    const { tab = 'home' } = useParams<RouteParams>();
    const [fullWidth, setFullWidth] = useState<boolean>(true);
    const [showClosePrimaryCourse, setShowClosePrimaryCourse] = useState<boolean>(true);
    const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState<boolean>(false);
    const [isContactSupportOpen, setIsContactSupportOpen] = useState<boolean>(false);
    const [isPrimaryCourseDialogOpen, setIsPrimaryCourseDialogOpen] = useState<boolean>(false);
    const [currentLeaderboardChallenge, setCurrentLeaderboardChallenge] = useState<coreService.ChallengeData>();
    const [isOpenLeaderboard, setIsOpenLeaderboard] = useState<boolean>(false);
    const [isOpenTournamentLeaderboard, setIsOpenTournamentLeaderboard] = useState<boolean>(false);
    const [certificateCourseUuid, setCertificateCourseUuid] = useState<string>(
        localStorage.getItem('courseCertificateUuid') || ''
    );
    const [isCustomCourse, setIsCustomCourse] = useState<boolean>(false);
    const [videoTutorialUuid, setVideoTutorialUuid] = useState<string>();
    const [learningPathData, setLearningPathData] = useState<{ date: string; name: string }>();
    const [skillTreeCertificateDate, setSkillTreeCertificateDate] = useState<string>();
    const [tournamentUuid, setTournamentUuid] = useState<string>();
    const [contactSupportDefaultValue, setContactSupportDefaultValue] = useState<
        { subject: string; description: string } | undefined
    >();
    const [isUpgradeContactSupport, setIsUpgradeContactSupport] = useState<boolean>(false);
    const token = useSelector(selectToken);
    const locale = useSelector(selectLocale);
    const leanUser = useSelector(selectIsLeanUser);
    const tenant = useSelector(selectCustomerDisplayName);
    const nickname = useSelector(selectUserNickname);
    const fullName = useSelector(selectUserFullName);
    const isPLG = useSelector(selectIsPLG);
    const isCxUniversity = useSelector(selectIsCxUniversity);
    const subject = translate('upgradeAccountContactSupportSubject');
    const description = translate('upgradeAccountContactSupportDescription');

    interface CertificateData {
        date: string;
        learningPathName: string;
    }

    const setCertificateUuid = (courseUuid: string) => {
        setCertificateCourseUuid(courseUuid);
    };

    const setCertificateData = ({ date, learningPathName }: CertificateData) => {
        setLearningPathData({ date, name: learningPathName });
    };

    const topsHeight = '7.6875rem';
    const topBarHeight = '4.375rem';

    const tabs = Object.values(MenuItems);
    const navigate = useNavigate();

    useNotificationsWebsocket(token);

    const onSelectTab = (item: keyof typeof MenuItems) => navigate(`/app/${String(item)}`);
    const leanTabs: (keyof typeof MenuItems)[] = ['home', 'course', 'learn', 'learningPath', 'quiz'];
    const noLeftSideTabs: (keyof typeof MenuItems)[] = ['learningPath', 'skillTree', 'quiz', 'contact-support'];

    useEffect(() => {
        if (fullWidth)
            window.setTimeout(() => {
                setShowClosePrimaryCourse(true);
            }, 250);
        else setShowClosePrimaryCourse(false);
    }, [fullWidth]);

    useEffect(() => {
        if (!isContactSupportOpen) {
            setContactSupportDefaultValue(undefined);
            setIsUpgradeContactSupport(false);
        }
    }, [isContactSupportOpen]);

    const checkTabAvailability = (tab: keyof typeof MenuItems) => {
        if (leanUser) return leanTabs.includes(tab);
        return tabs.includes(tab);
    };

    const onContactUsClick = () => {
        setIsUpgradeContactSupport(true);
        setContactSupportDefaultValue({
            subject,
            description
        });
        setIsContactSupportOpen(true);
    };

    const translationMap = [
        { flag: isPLG, key: 'plgLeanBannerText' },
        { flag: isCxUniversity, key: 'CxUniversityText' }
    ];

    const translationTextKey = translationMap.find(item => item.flag)?.key || 'leanBannerText';

    return checkTabAvailability(tab) ? (
        <>
            {isPrimaryCourseDialogOpen && (
                <PrimaryCourseDialog setIsPrimaryCourseDialogOpen={setIsPrimaryCourseDialogOpen} />
            )}
            <RightSidePanel
                isOpen={isOpenLeaderboard || !!currentLeaderboardChallenge || isOpenTournamentLeaderboard}
                onClose={() => {
                    setIsOpenLeaderboard(false);
                    setCurrentLeaderboardChallenge(undefined);
                    setIsOpenTournamentLeaderboard(false);
                }}
            >
                {isOpenLeaderboard && (
                    <Leaderboard
                        isOpenTournamentLeaderboard={isOpenTournamentLeaderboard}
                        tournamentUuid={tournamentUuid}
                        onClose={() => setIsOpenLeaderboard(false)}
                    />
                )}
                {currentLeaderboardChallenge && (
                    <ChallengeLeaderboard
                        currentChallenge={currentLeaderboardChallenge}
                        onClose={() => setCurrentLeaderboardChallenge(undefined)}
                    />
                )}
                {isOpenTournamentLeaderboard && (
                    <Leaderboard
                        isOpenTournamentLeaderboard={isOpenTournamentLeaderboard}
                        tournamentUuid={tournamentUuid}
                        onClose={() => setIsOpenTournamentLeaderboard(false)}
                    />
                )}
            </RightSidePanel>
            {isAccountSettingsOpen && <AccountSettingsContainer setIsAccountSettingsOpen={setIsAccountSettingsOpen} />}
            {isContactSupportOpen && (
                <ContactSupportContainer
                    setIsContactSupportOpen={setIsContactSupportOpen}
                    defaultValues={contactSupportDefaultValue}
                    isUpgrade={isUpgradeContactSupport}
                />
            )}
            {certificateCourseUuid && (
                <CertificatePopupContainer
                    onClose={() => {
                        setCertificateUuid('');
                        setIsCustomCourse(false);
                    }}
                    setIsCustomCourse={setIsCustomCourse}
                    courseUuid={certificateCourseUuid}
                    isCustom={isCustomCourse}
                    isSkillTree={false}
                    isLearningPath={false}
                />
            )}
            {videoTutorialUuid && (
                <VideoTutorialPopup setVideoTutorialUuid={setVideoTutorialUuid} videoTutorialUuid={videoTutorialUuid} />
            )}
            {learningPathData && (
                <CertificatePopupContainer
                    inputCertificateData={{
                        name: fullName || nickname,
                        completedAt: learningPathData.date,
                        entityDisplayName: learningPathData.name
                    }}
                    onClose={() => setLearningPathData(undefined)}
                    isCustom={false}
                    isSkillTree={false}
                    isLearningPath={true}
                />
            )}
            {skillTreeCertificateDate && (
                <CertificatePopupContainer
                    inputCertificateData={{
                        name: fullName || nickname,
                        entityDisplayName: '',
                        completedAt: skillTreeCertificateDate || ''
                    }}
                    onClose={() => setSkillTreeCertificateDate(undefined)}
                    isCustom={false}
                    isSkillTree={true}
                    isLearningPath={false}
                />
            )}
            {tab === 'quiz' && <QuizContainer />}
            {tab !== 'quiz' && (
                <div className={styles['learning-panel']}>
                    {leanUser && (
                        <LeanBanner
                            translationTextKey={translationTextKey}
                            setIsContactSupportOpen={setIsContactSupportOpen}
                            setContactSupportDefaultValues={setContactSupportDefaultValue}
                            setIsUpgradeContactSupport={setIsUpgradeContactSupport}
                        />
                    )}

                    <TopBannerContainer
                        setVideoTutorialUuid={setVideoTutorialUuid}
                        selectedTab={tab}
                        setSelectedTab={onSelectTab}
                        setIsAccountSettingsOpen={setIsAccountSettingsOpen}
                        setIsContactSupportOpen={setIsContactSupportOpen}
                    />

                    <div className={styles.bottom}>
                        {!noLeftSideTabs.includes(tab) && (
                            <LeftSideExpandedPanel
                                onOpen={() => setFullWidth(false)}
                                onClose={() => setFullWidth(true)}
                                isOpen={!fullWidth}
                                top={leanUser ? topsHeight : topBarHeight}
                            >
                                {showClosePrimaryCourse ? (
                                    <PrimaryCourseCloseContainer
                                        setIsPrimaryCourseDialogOpen={setIsPrimaryCourseDialogOpen}
                                    />
                                ) : (
                                    <PrimaryCourseContainer
                                        onChangePrimaryCourse={setIsAccountSettingsOpen}
                                        setIsPrimaryCourseDialogOpen={setIsPrimaryCourseDialogOpen}
                                    />
                                )}
                            </LeftSideExpandedPanel>
                        )}

                        <div
                            className={
                                styles[
                                    noLeftSideTabs.includes(tab)
                                        ? 'content-wrapper-no-left'
                                        : fullWidth
                                        ? 'content-wrapper-full-width'
                                        : 'content-wrapper'
                                ]
                            }
                            style={{ '--bannersHeight': leanUser ? topsHeight : topBarHeight } as any}
                        >
                            {tab === 'home' && (
                                <div className={styles.content}>
                                    <HomeContainer
                                        setCertificateCourseUuid={setCertificateCourseUuid}
                                        setIsCustomCourse={setIsCustomCourse}
                                        setVideoTutorialUuid={setVideoTutorialUuid}
                                        onContactUsClick={onContactUsClick}
                                        fullWidth={fullWidth}
                                    />
                                </div>
                            )}
                            {tab === 'course' && (
                                <div className={styles.content}>
                                    <Course onContactUsClick={onContactUsClick} />
                                </div>
                            )}
                            {tab === 'test' && (
                                <div className={styles.content}>
                                    <AssessmentPageContainer />
                                </div>
                            )}
                            {tab === 'play' && (
                                <div className={styles.content}>
                                    <Play setCurrentChallenge={setCurrentLeaderboardChallenge} />
                                </div>
                            )}
                            {tab === 'tournament' && (
                                <div className={styles.content}>
                                    <TournamentsPage
                                        setIsOpenTournamentLeaderboard={setIsOpenTournamentLeaderboard}
                                        setTournamentUuid={setTournamentUuid}
                                        locale={locale}
                                    />
                                </div>
                            )}
                            {tab === 'learn' && (
                                <div className={styles.content}>
                                    <DiscoverContainer
                                        setIsPrimaryCourseDialogOpen={setIsPrimaryCourseDialogOpen}
                                        setCertificateCourseUuid={setCertificateUuid}
                                        setIsCustomCourse={setIsCustomCourse}
                                        setLearningPathCertificateDate={setCertificateData}
                                        setVideoTutorialUuid={setVideoTutorialUuid}
                                        setSkillTreeCertificateDate={setSkillTreeCertificateDate}
                                        onContactUsClick={onContactUsClick}
                                    />
                                </div>
                            )}
                            {tab === 'learningPath' && (
                                <div className={styles.content}>
                                    <LearningPathContainer />
                                </div>
                            )}
                            {tab === 'skillTree' && (
                                <div className={styles.content}>
                                    <SkillTreeNavigationContainer />
                                </div>
                            )}
                            {tab === 'contact-support' && (
                                <div className={styles.content}>
                                    <ContactSupportContainer
                                        setIsContactSupportOpen={setIsContactSupportOpen}
                                        redirectToHomeOnClose
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    ) : (
        <Navigate to="/app/not-found" />
    );
};

type RouteParams = {
    tab: keyof typeof MenuItems;
};

export default AppContainer;
